import { bound } from "@frui.ts/helpers/dist";
import type ScreenBase from "@frui.ts/screens/dist/structure/screenBase";
import type { EntityNames } from "config";
import { Modules } from "config";
import { action, computed, observable, reaction } from "mobx";
import type EventBus from "services/eventBus";
import { allRefundApprovalsApproved, approved, approvedRefundsExported } from "services/eventBus";
import type LocalizationService from "services/localizationService";
import type { IApprovable } from "entities/types";
import { ApprovalState } from "entities/approvalState";
import Exportable from "entities/exportable";
import type RefundsSearchFilter from "entities/filters/refunds/refundsSearchFilter";
import type ApprovalService from "services/approvalService";
import type CountrySelectViewModel from "../countrySelectViewModel";
import ListViewModel from "../listViewModel";

export default abstract class ApprovalListViewModel<
  TApprovalType,
  TEntity extends IApprovable<TApprovalType>,
  TDetail extends ScreenBase,
  TFilter extends RefundsSearchFilter
> extends ListViewModel<TEntity, TDetail, ScreenBase, TFilter> {
  protected readonly modelName: EntityNames;
  protected securityService = this.approvalService.securityService;
  countrySelectViewModel: CountrySelectViewModel;
  abstract readonly approvalType: any;
  @observable _approvalState?: ApprovalState;
  @observable isAnyExportable = false;

  protected constructor(
    protected localizationService: LocalizationService,
    protected eventBus: EventBus,
    protected approvalService: ApprovalService<any, TEntity>,
    protected countrySelectViewModelFactory: ReturnType<typeof CountrySelectViewModel.Factory>
  ) {
    super();
    this.countrySelectViewModel = this.countrySelectViewModelFactory("id");
  }

  onInitialize() {
    this.unsubscriptions.push(
      this.eventBus.subscribe(approved, this.onRefundApproved),
      this.eventBus.subscribe(allRefundApprovalsApproved, this.onGroupActionSuccess),
      this.eventBus.subscribe(approvedRefundsExported, this.onGroupActionSuccess),
      reaction(() => this._approvalState, this.approvalStateReaction)
    );
    super.onInitialize();
  }

  @bound
  approvalStateReaction() {
    switch (this._approvalState) {
      case ApprovalState.TO_APPROVE:
        this.filter.approvedBy = this.approvalService.currentApproverPredecessor();
        this.filter.notApprovedBy = this.approvalService.whoAmI();
        break;
      case ApprovalState.APPROVED:
        this.filter.approvedBy = this.approvalService.whoAmI();
        this.filter.notApprovedBy = undefined;
        break;
      default:
        this.filter.approvedBy = undefined;
        this.filter.notApprovedBy = undefined;
        break;
    }
  }

  protected resetFilterValues(filter: TFilter) {
    this._approvalState = undefined;
    super.resetFilterValues(filter);
  }

  @bound
  async onRefundApproved(event: ReturnType<typeof approved>) {
    if (this.activeChild) {
      this.closeChild(this.activeChild);
    }

    // Clear all selected options to not keep any old ones.
    (this as any).self?.selectedItems?.clear();

    await this.loadData();
    const approvedItem = this.items.find(item => item.id === event.payload.approvable.id);

    const role = this.approvalService.whoAmI();
    if (approvedItem && role && !(approvedItem.approvals ?? []).flatMap(approval => approval.approvalType).includes(role)) {
      approvedItem.approvals.push({ approvalType: role });
    }
  }

  protected parseFilterParam(paramName: keyof TFilter | string, value: any) {
    if ((paramName as string).includes("variableSymbol")) {
      return parseInt(value);
    } else {
      return super.parseFilterParam(paramName, value);
    }
  }

  @bound
  async onGroupActionSuccess() {
    if (this.activeChild) {
      this.closeChild(this.activeChild);
    }
    await this.loadData();
  }

  @computed
  get noApprovalsForExport() {
    return !this.isAnyExportable;
  }

  @computed
  get allowDelete() {
    return false;
  }

  @bound
  allowItemDetail(item: TEntity) {
    return false;
  }

  @computed
  get allowApprove() {
    return this.approvalService.allowWrite();
  }

  translate(code: string, size?: number | undefined): string {
    return this.localizationService.translateModule(Modules.finance, `refundApprovals.${code}`, size);
  }

  countryCode(countryId: string) {
    return this.approvalService.countryCode(countryId);
  }

  @bound
  canApprove(item: TEntity) {
    return this.approvalService.canApprove(item);
  }

  @bound
  myApprovalStep(item: TEntity) {
    return this.approvalService.myApprovalStep(item);
  }

  @bound
  approvedByMe(item: TEntity) {
    return this.approvalService.approvedByMe(item);
  }

  @bound
  isExported(item: Exportable) {
    return false;
  }

  @action.bound
  approveClick = (item: TEntity) => async () => {
    await this.approvalService.approve(this.repository, item, {
      title: this.translateModuleGeneral("confirmApproval.title"),
      confirm: this.translateModuleGeneral("confirmApproval.confirm"),
      approved: this.translateModuleGeneral("candidateApproved"),
    });
  };

  @bound
  approvedByApprover(approvable: TEntity, approver: TApprovalType) {
    return this.approvalService.approvedByApprover(approvable, approver);
  }

  @computed
  get name() {
    return this.currentPaging?.totalItems && this.currentPaging.totalItems > 0
      ? this.translate("nameCount").replace("%count%", this.currentPaging.totalItems.toString())
      : this.translate("name");
  }

  @bound
  styleForRefundApprovalIndicator(refund: TEntity, approver: TApprovalType) {
    return (
      "rounded font-weight-bold py-2 px-3 mx-1 my-1 " +
      (this.approvalService.approvedByApprover(refund, approver)
        ? "btn-success text-success"
        : "border-0 bg-transparent text-muted")
    );
  }

  @computed
  get approvalsOptions() {
    return this.approvalService.approvalsOptions();
  }

  @bound
  translateModuleGeneral(code: string, size?: number): string {
    return this.translate(code, size);
  }
}
